import React from "react";
import { useTranslation } from 'react-i18next';
import { Input } from "../../components/ui/input";
import { useCashierSellingViewModel } from "./CashierSellingViewModel";
import { canConfirmCashPay, effectiveCashGiven } from "./effectiveCashGiven";
import { decrementCartQuantity } from "./cartQuantity";
import { specialProductAccent } from "./freeSaleServices";
import {
  defaultFreePriceDesignation,
  FREE_PRICE_ACCENT,
} from "./freePriceSale";
import { FreePriceProductModal } from "./FreePriceProductModal";
import { formatCurrency } from "../../lib/currency";
import { salePriceTtcFromHt, cartLineTotalTtc, calcCartTotalsFromRoundedTtc, productSearchHaystackMatches, buildProductSearchHaystack } from "@shared/utils";
import { PaymentMethod, type ProductCategory, type ProductWithStock } from "../../../domain/models";
import {
  ShoppingCart,
  Search,
  X,
  Check,
  Trash2,
  ScanLine,
  RefreshCw,
  Package,
  CheckCircle,
  History,
  CreditCard,
  Minus,
  Plus,
  ArrowLeft,
  Printer,
  FileText,
  XCircle,
  Tag,
  AlertTriangle,
} from "lucide-react";

import { PosTopBar, type PosScreen } from "../../components/PosTopBar";
import { quitDesktopApp } from "../../components/DesktopCloseButton";
import { PosProfileModal, type PosProfileFormValues } from "../../components/PosProfileModal";
import { PosBackButton } from "../../components/PosBackButton";
import { ComingLaterBanner, StockHealth } from "../../components/figma-ui";
import { InvoiceModal, type InvoiceSale } from "../../components/figma-extended";
import { ReceiptStatus, type Receipt } from "./CashierSellingViewModelState";
import { useAuth } from "../../contexts";
import { databaseConfig } from "../../../config/database";
import { supportsOfflinePos } from "../../../runtime/frontofficeRuntime";
import { getLastCashClosure, type CashClosureRecord } from "../../../data/api/salesApiClient";
import { PosClotureScreen } from "../cloture/PosClotureScreen";
import { CLOSURE_CREATED_EVENT } from "../cloture/cloture-helpers";
import { PosStockCountScreen } from "../stockcount";
import { listBranchStockCounts } from "../../../data/api/stockCountApiClient";
import { findActiveSession } from "../stockcount/stockCountTypes";
import { PosRefundScreen } from "./PosRefundScreen";
import { formatReceiptDateTime, isReceiptRefunded } from "./receiptRefundHelpers";
import { useOfflinePos } from "../../../offline/OfflinePosContext";
import {
  TERMINAL_RESET_EVENT,
  TERMINAL_SETUP_CHANGED_EVENT,
  readTerminalSetup,
  resetTerminal,
} from "../login/terminalSetup";
import { decideStockBarcodePrint } from "../../../printing/stockBarcodePrint";
import { printProductBarcodeLabel } from "../../../data/api/barcodeLabelApiClient";
import { clearPosInstallation } from "../../../data/api/posInstallationApi";
import { readPosDeviceId } from "../../../printing/posDeviceId";
const DISC_OPTIONS = [0, 5, 10, 15, 20, 25, 30];

const CashierSellingScreen: React.FC = () => {
  const { t } = useTranslation();
  const viewModel = useCashierSellingViewModel();
  const { state, intents } = viewModel;
  const { userSession, updateSessionUser } = useAuth();
  const offlinePos = useOfflinePos();
  const electronOffline = supportsOfflinePos();
  const userRole = userSession?.user.role ?? 'cashier';
  const accessToken = userSession?.token.accessToken ?? '';
  const branchId = userSession?.branchId ?? '';

  // Pull catalog + push pending ops when online (including after offline session refresh).
  React.useEffect(() => {
    if (!branchId) return;
    if (!accessToken) return;
    void offlinePos.syncNow(accessToken, branchId).catch((err) => {
      console.warn('Offline POS sync failed:', err);
    });
  }, [accessToken, branchId, offlinePos.isOnline, offlinePos.syncNow]);

  const [posScreen, setPosScreen] = React.useState<PosScreen>('selling');
  const [lastClosure, setLastClosure] = React.useState<CashClosureRecord | null>(null);
  const [activeStockCount, setActiveStockCount] = React.useState<{
    visibleId: string;
    percent: number;
  } | null>(null);
  const [lastCompletedReceiptId, setLastCompletedReceiptId] = React.useState('');
  const [invoiceSale, setInvoiceSale] = React.useState<InvoiceSale | null>(null);
  const [historySearch, setHistorySearch] = React.useState('');
  const [printingReceiptId, setPrintingReceiptId] = React.useState<string | null>(null);
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [freePriceCategory, setFreePriceCategory] =
    React.useState<ProductCategory | null>(null);
  const [profileSaving, setProfileSaving] = React.useState(false);
  const [profileError, setProfileError] = React.useState('');
  const profileOpenRef = React.useRef(false);
  profileOpenRef.current = profileOpen;
  const freePriceModalOpenRef = React.useRef(false);
  freePriceModalOpenRef.current = freePriceCategory !== null;

  const profileFullName = React.useMemo(() => {
    const u = userSession?.user;
    if (!u) return '';
    if (u.firstName || u.lastName) {
      return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
    }
    return u.username ?? '';
  }, [userSession?.user]);

  const onSaveProfile = React.useCallback(
    async (values: PosProfileFormValues) => {
      if (!accessToken) return;
      const offlineMsg = offlinePos.requireOnline('profile');
      if (offlineMsg) {
        setProfileError(offlineMsg);
        return;
      }
      setProfileSaving(true);
      setProfileError('');
      try {
        const baseUrl = databaseConfig.mongodb.apiBaseUrl;
        const body: Record<string, string> = {
          name: values.name,
          email: values.email,
        };
        if (values.newPassword) {
          body.currentPassword = values.currentPassword;
          body.newPassword = values.newPassword;
        }

        const res = await fetch(`${baseUrl}/api/users/me`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          const msg =
            (errBody as { message?: string | string[] }).message ??
            'Échec de la mise à jour du profil';
          setProfileError(Array.isArray(msg) ? msg.join(', ') : String(msg));
          return;
        }

        const payload = await res.json().catch(() => ({}));
        const updated = (payload as { data?: { name?: string; email?: string } }).data;
        const nextName = updated?.name ?? values.name;
        const nextEmail = updated?.email ?? values.email;
        const parts = nextName.trim().split(/\s+/);
        updateSessionUser({
          email: nextEmail,
          firstName: parts[0] ?? nextName,
          lastName: parts.slice(1).join(' '),
        });
        setProfileOpen(false);
      } catch {
        setProfileError('Erreur réseau lors de la mise à jour du profil');
      } finally {
        setProfileSaving(false);
      }
    },
    [accessToken, updateSessionUser, offlinePos],
  );
  /** When opening refund from history, start at review step */
  const [refundStartAtReview, setRefundStartAtReview] = React.useState(false);
  /** Sync prefill — avoids race where PosRefund mounts before ViewModel selectedReceipt updates */
  const [refundPrefillReceipt, setRefundPrefillReceipt] = React.useState<Receipt | null>(null);
  /** Figma: session-level refunded receipt ids — persists across PosRefund remounts */
  const [refundedIds, setRefundedIds] = React.useState<Set<string>>(() => new Set());
  const [invPage, setInvPage] = React.useState(1);
  const [barcodePrintTarget, setBarcodePrintTarget] = React.useState<ProductWithStock | null>(null);
  const [barcodePrintBusy, setBarcodePrintBusy] = React.useState(false);
  const [barcodePrintMessage, setBarcodePrintMessage] = React.useState('');
  /** Figma: product id awaiting zero-stock force-add confirm — removed; add-to-cart is always direct. */
  const branchName = userSession?.branchName ?? userSession?.branchId ?? '';
  const INV_PER_PAGE = 9;
  /** Selling screen: keep barcode scans landing in the product search field. */
  const productSearchRef = React.useRef<HTMLInputElement>(null);
  const searchTermRef = React.useRef(state.searchTerm);
  const posScreenRef = React.useRef(posScreen);
  const lastSearchKeyAtRef = React.useRef(0);
  searchTermRef.current = state.searchTerm;
  posScreenRef.current = posScreen;

  const focusProductSearch = React.useCallback(() => {
    if (posScreenRef.current !== 'selling') return;
    // Profile modal owns keyboard/focus while open — do not steal caret for scanner parking.
    if (profileOpenRef.current || freePriceModalOpenRef.current) return;
    const el = productSearchRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    el.focus({ preventScroll: true });
  }, []);

  React.useEffect(() => {
    if (posScreen === 'history') {
      void intents.onLoadReceiptHistory();
    }
    if (posScreen === 'inventory') {
      void intents.onLoadAllProducts();
      setInvPage(1);
    }
  }, [posScreen]);

  // Always park the caret in product search while the selling screen is visible,
  // so USB scanners type into the lookup field regardless of prior clicks.
  // Suspended while the profile modal is open.
  React.useEffect(() => {
    if (posScreen !== 'selling') return;
    if (profileOpen) return;
    focusProductSearch();
    const boot = window.setTimeout(focusProductSearch, 40);
    return () => window.clearTimeout(boot);
  }, [posScreen, profileOpen, focusProductSearch]);

  React.useEffect(() => {
    if (posScreen !== 'selling') return;

    const isAllowedAwayFocus = () => {
      if (profileOpenRef.current || freePriceModalOpenRef.current) return true;
      const active = document.activeElement as HTMLElement | null;
      if (!active || active === document.body) return false;
      if (active === productSearchRef.current) return true;
      // Cart line remises <select> needs temporary focus
      if (active.tagName === 'SELECT') return true;
      return false;
    };

    const reclaimIfNeeded = () => {
      if (posScreenRef.current !== 'selling') return;
      if (profileOpenRef.current || freePriceModalOpenRef.current) return;
      if (isAllowedAwayFocus()) return;
      focusProductSearch();
    };

    const onFocusIn = () => {
      window.setTimeout(reclaimIfNeeded, 0);
    };
    const onFocusOut = () => {
      window.setTimeout(reclaimIfNeeded, 50);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (posScreenRef.current !== 'selling') return;
      if (profileOpenRef.current || freePriceModalOpenRef.current) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      if (target === productSearchRef.current) {
        // Scanner terminator → add exact barcode/SKU to cart (not filter).
        if (event.key === 'Enter') {
      event.preventDefault();
          void intents.onBarcodeScanSubmit();
        }
        return;
      }
      if (target?.tagName === 'SELECT') return;

      const isPrintable = event.key.length === 1;
      if (!isPrintable && event.key !== 'Backspace' && event.key !== 'Enter') return;

      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        focusProductSearch();
        void intents.onBarcodeScanSubmit();
        return;
      }

      // Scanner burst (or typing) while focus is on a button/card → route into search.
      event.preventDefault();
      event.stopPropagation();
      focusProductSearch();

      const now = Date.now();
      const prev = searchTermRef.current;
      const prevIsBarcode = /^[0-9]{8,}$/.test(prev.trim());
      const idleNewScan = prevIsBarcode && now - lastSearchKeyAtRef.current >= 280;
      lastSearchKeyAtRef.current = now;

      if (isPrintable) {
        const next = idleNewScan ? event.key : `${prev}${event.key}`;
        intents.onProductSearch(next);
      } else if (event.key === 'Backspace') {
        intents.onProductSearch(idleNewScan ? '' : prev.slice(0, -1));
      }
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [posScreen, focusProductSearch, intents]);

  // After a successful scan-to-cart, keep the search field focused for the next scan.
  React.useEffect(() => {
    if (posScreen !== 'selling') return;
    if (profileOpen) return;
    if (state.searchTerm.trim()) return;
    const t = window.setTimeout(() => focusProductSearch(), 40);
    return () => window.clearTimeout(t);
  }, [posScreen, profileOpen, state.searchTerm, state.cart, focusProductSearch]);

  // Seed / sync refundedIds from receipt history (REFUNDED status)
  React.useEffect(() => {
    setRefundedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const r of state.receiptHistory) {
        if (r.status === ReceiptStatus.REFUNDED) {
          if (!next.has(r.receiptNumber)) {
            next.add(r.receiptNumber);
            changed = true;
          }
          if (!next.has(r.id)) {
            next.add(r.id);
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [state.receiptHistory]);

  const refreshLastClosure = React.useCallback(() => {
    if (!accessToken || !branchId || userRole === 'cashier') return;
    getLastCashClosure(accessToken, branchId)
      .then(setLastClosure)
      .catch(() => setLastClosure(null));
  }, [accessToken, branchId, userRole]);

  const refreshActiveStockCount = React.useCallback(() => {
    if (!accessToken || !branchId || userRole === 'cashier') {
      setActiveStockCount(null);
      return;
    }
    listBranchStockCounts(accessToken, branchId, { page: 1, limit: 100 })
      .then((listed) => {
        const active = listed.active ?? findActiveSession(listed.sessions);
        setActiveStockCount(
          active
            ? { visibleId: active.visibleId, percent: active.progress.percent }
            : null,
        );
      })
      .catch(() => setActiveStockCount(null));
  }, [accessToken, branchId, userRole]);

  React.useEffect(() => {
    refreshLastClosure();
    refreshActiveStockCount();
  }, [refreshLastClosure, refreshActiveStockCount, posScreen]);

  React.useEffect(() => {
    const handler = () => refreshLastClosure();
    window.addEventListener(CLOSURE_CREATED_EVENT, handler);
    return () => window.removeEventListener(CLOSURE_CREATED_EVENT, handler);
  }, [refreshLastClosure]);

  // Navigate to success screen after payment (not refund — refund wizard has its own done step)
  React.useEffect(() => {
    if (state.notification.show && state.notification.type === 'success') {
      const match = state.notification.message.match(/^Sale completed \(([^)]+)\)/);
      if (match?.[1]) {
        setLastCompletedReceiptId(match[1]);
        setPosScreen('success');
      }
    }
  }, [state.notification.show, state.notification.type, state.notification.message]);

  // Keyboard → cash keypad (modal or inline Figma checkout)
  React.useEffect(() => {
    const keypadActive =
      state.showCashKeypad ||
      (posScreen === 'checkout' && state.paymentMethod === PaymentMethod.CASH);
    if (!keypadActive) return;

    const handleKeydown = (event: KeyboardEvent) => {
      const key = event.key;
      if (/^[0-9]$/.test(key)) {
        event.preventDefault();
        intents.onCashKeypadPress(key);
      } else if (key === '.' || key === ',') {
        event.preventDefault();
        intents.onCashKeypadPress('.');
      } else if (key === 'Backspace') {
        event.preventDefault();
        intents.onCashKeypadBackspace();
      } else if (key === 'Delete') {
        event.preventDefault();
        intents.onCashKeypadClear();
      } else if (key === 'Enter' && state.showCashKeypad) {
        event.preventDefault();
        intents.onCashGivenChanged(parseFloat(state.cashKeypadValue) || 0);
        intents.onHideCashKeypad();
      } else if (key === 'Escape' && state.showCashKeypad) {
        event.preventDefault();
        intents.onHideCashKeypad();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [state.showCashKeypad, state.cashKeypadValue, state.paymentMethod, posScreen, intents]);

  // Format currency using the new internationalized currency system
  const formatPrice = (amount: number, currencyCode: string) => formatCurrency(amount, currencyCode);

  // Notification component
  const renderNotification = () => {
    if (!state.notification.show) return null;

    const bgColor = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500'
    }[state.notification.type];

    return (
      <div className={`fixed top-20 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 max-w-md animate-in slide-in-from-right duration-300`}>
        <div className="flex items-center space-x-2">
          {state.notification.type === 'success' && <Check className="w-5 h-5" />}
          {state.notification.type === 'error' && <X className="w-5 h-5" />}
          {state.notification.type === 'info' && <ShoppingCart className="w-5 h-5" />}
          <span className="font-medium">{state.notification.message}</span>
        </div>
      </div>
    );
  };

  const renderHeader = () => (
    <PosTopBar
      cashierName={state.cashierName}
      branchName={branchName}
      userRole={userRole}
      mode={posScreen === 'refund' || state.operationMode === 'REFUND' ? 'refund' : 'sale'}
      lastClosure={lastClosure ? {
        confirmedAt: lastClosure.confirmedAt,
        difference: lastClosure.difference,
        status: lastClosure.status,
      } : null}
      activeStockCount={activeStockCount}
      isOnline={electronOffline ? offlinePos.isOnline : true}
      pendingSyncCount={electronOffline ? offlinePos.pendingCount : 0}
      guardOnlineAction={electronOffline ? offlinePos.requireOnline : () => null}
      onOnlineRequiredMessage={(message) => {
        window.alert(message);
      }}
      onInventory={() => setPosScreen('inventory')}
      onStockCount={() => setPosScreen('stockcount')}
      onCloture={() => setPosScreen('cloture')}
      onLogout={intents.onLogout}
      onQuitApp={quitDesktopApp}
      onResetTerminal={() => {
        const deviceId = readPosDeviceId();
        const tokenForClear = accessToken;
        resetTerminal();
        try {
          sessionStorage.removeItem('cashier_session');
        } catch {
          /* sessionStorage can be unavailable */
        }
        if (deviceId && tokenForClear) {
          void clearPosInstallation(tokenForClear, deviceId);
        }
        intents.onLogout();
        window.dispatchEvent(new Event(TERMINAL_RESET_EVENT));
        window.dispatchEvent(new Event(TERMINAL_SETUP_CHANGED_EVENT));
      }}
      onEditProfile={() => {
        setProfileError('');
        setProfileOpen(true);
      }}
    />
  );

  const resolveSku = (productId: string) =>
    state.products.find((p) => p.id === productId)?.sku ?? productId;

  const categoryLabel = (categoryId?: string) => {
    if (!categoryId) return '—';
    return state.categories.find((c) => c.id === categoryId)?.name ?? categoryId;
  };

  /** Line / cart subtotals from rounded PU TTC (not HT × (1+TVA)). */
  const cartTotals = calcCartTotalsFromRoundedTtc(state.cart, state.discountPercentage);
  const productRemisesTotal = cartTotals.productDiscountTtc;
  const subtotalTtc = cartTotals.subtotalTtc;
  const cartRemiseTtc = cartTotals.cartDiscountTtc;

  // Zone 1: Cart Section (Figma POSSelling right panel)
  const renderCartSection = () => (
    <div className="flex flex-col bg-gray-900 overflow-hidden h-full">
      <div className="flex-1 overflow-y-auto">
        {state.cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 px-8 text-center py-12">
            <div className="w-14 h-14 bg-gray-800 rounded-2xl flex items-center justify-center">
              <ShoppingCart size={20} className="text-gray-700" />
            </div>
            <div className="text-gray-600 text-sm">{t('pos.cart.empty_title')}</div>
            <div className="text-gray-700 text-xs">{t('pos.cart.empty_hint')}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[300px]">
            <thead className="sticky top-0 bg-gray-900 z-10">
              <tr className="border-b border-gray-800">
                {['#', 'QTE', t('pos.cart.product'), t('pos.cart.pu_ttc'), t('pos.cart.disc'), t('pos.cart.total')].map((h) => (
                  <th
                    key={h}
                    className={`py-2.5 text-[9px] font-bold text-gray-600 uppercase tracking-wider ${
                      h === t('pos.cart.product') ? 'text-left pl-3 pr-1' : 'text-center px-1.5'
                    }`}
                  >
                    {h}
                  </th>
                ))}
            </tr>
          </thead>
            <tbody className="divide-y divide-gray-800/50">
              {state.cart.map((item, index) => {
                const unitPriceTtc = salePriceTtcFromHt(item.unitPrice, item.taxRate || 0);
                const lineTotal = cartLineTotalTtc(item);
                const systemStock =
                  item.availableStock ??
                  state.products.find((p) => p.id === item.productId)?.branchStock?.availableStock ??
                  state.products.find((p) => p.id === item.productId)?.branchStock?.stock ??
                  0;
                const overStock = item.quantity > systemStock;
                return (
                  <tr key={`${item.productId}-${index}`} className="hover:bg-gray-800/40 transition-colors group">
                    <td className="py-3 text-center text-xs text-gray-600 px-1.5 tabular-nums">{index + 1}</td>
                    <td className="py-3 text-center px-1.5">
                      <div className="flex flex-col items-center gap-0.5">
                        <div className="flex items-center justify-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => {
                              const nextQuantity = decrementCartQuantity(item.quantity);
                              if (nextQuantity === null) {
                                intents.onRemoveFromCart(index);
                              } else {
                                intents.onUpdateCartQuantity(index, nextQuantity);
                              }
                            }}
                            disabled={state.operationMode === 'REFUND'}
                            className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded flex items-center justify-center text-gray-400 shrink-0 transition-colors disabled:opacity-40"
                          >
                            <Minus size={8} />
                          </button>
                          <span
                            className={`w-7 text-center text-sm font-bold tabular-nums ${
                              overStock ? 'text-amber-400' : 'text-white'
                            }`}
                          >
                            {item.quantity}
                          </span>
                        <button
                            type="button"
                            onClick={() => intents.onUpdateCartQuantity(index, item.quantity + 1)}
                            disabled={state.operationMode === 'REFUND'}
                            className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded flex items-center justify-center text-gray-400 shrink-0 transition-colors disabled:opacity-40"
                          >
                            <Plus size={8} />
                        </button>
                        </div>
                        {overStock && (
                          <div className="flex items-center gap-0.5 text-[9px] text-amber-500 font-semibold leading-none">
                            <AlertTriangle size={8} />
                            {systemStock <= 0
                              ? t('pos.cart.overstock_rupture')
                              : t('pos.cart.overstock_max', { max: systemStock })}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pl-3 pr-1 max-w-0">
                      <div className="text-xs font-semibold text-gray-200 truncate leading-snug">{item.productName}</div>
                      <div className="text-[9px] text-gray-600 font-mono mt-0.5">{resolveSku(item.productId)}</div>
                    </td>
                    <td className="py-3 text-center text-xs tabular-nums text-gray-300 px-1.5 whitespace-nowrap">
                      {formatPrice(unitPriceTtc, 'TND')}
                    </td>
                    <td className="py-3 text-center px-1.5">
                      <select
                        value={item.productDiscountPercentage}
                        onChange={(e) => intents.onUpdateCartItemDiscount(index, parseInt(e.target.value, 10))}
                        disabled={state.operationMode === 'REFUND'}
                        className="w-[52px] text-center text-xs font-bold bg-gray-800 border border-gray-700 rounded-md py-1 text-amber-400 appearance-none cursor-pointer focus:outline-none focus:border-amber-500 hover:border-gray-600 transition-colors disabled:opacity-40"
                      >
                        {DISC_OPTIONS.map((d) => (
                          <option key={d} value={d}>{d}%</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 text-center text-sm font-bold tabular-nums text-emerald-400 px-1.5 whitespace-nowrap">
                      {formatPrice(lineTotal, 'TND')}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <div className="border-t border-gray-800 bg-gray-900 shrink-0">
      {state.cart.length > 0 && (
          <div className="px-4 py-3 space-y-1.5">
            <div className="flex justify-between text-xs text-gray-500">
              <span>{t('pos.cart.subtotal')}</span>
              <span className="tabular-nums">{formatPrice(subtotalTtc, 'TND')}</span>
            </div>
            {productRemisesTotal > 0 && (
              <div className="flex justify-between text-xs text-amber-500">
                <span className="flex items-center gap-1"><Tag size={10} />{t('pos.cart.product_discounts')}</span>
                <span className="tabular-nums">-{formatPrice(productRemisesTotal, 'TND')}</span>
            </div>
            )}
            <div className="border-t border-gray-700 pt-2 flex justify-between items-center">
              <span className="text-sm font-bold text-gray-200">{t('pos.cart.total_ttc')}</span>
              <span className="text-2xl font-bold tabular-nums text-brand">{formatPrice(state.grandTotal, 'TND')}</span>
            </div>
          </div>
        )}
        <div className="px-4 pb-4 pt-2">
          <button
            type="button"
            onClick={() => {
              intents.onPaymentMethodSelected(PaymentMethod.CASH);
              intents.onCashKeypadClear();
              setPosScreen('checkout');
            }}
            disabled={state.cart.length === 0}
            className="w-full h-14 bg-brand hover:bg-brand-light disabled:opacity-25 disabled:cursor-not-allowed text-white font-bold text-base rounded-xl transition-all flex items-center justify-center gap-2.5 shadow-lg shadow-brand/20 active:scale-[0.98]"
          >
            <CreditCard size={18} />
            {t('pos.cart.proceed_payment')}
            {state.cart.length > 0 && (
              <span className="tabular-nums opacity-80">— {formatPrice(state.grandTotal, 'TND')}</span>
            )}
          </button>
          </div>
        </div>
    </div>
  );

  const renderProductSection = () => (
    <div className="h-full flex flex-col bg-gray-950 min-w-0">
      <div className="px-3 xl:px-5 pt-3 xl:pt-4 pb-2 xl:pb-3">
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            ref={productSearchRef}
            autoFocus
            placeholder="Rechercher nom, SKU ou scanner…"
            value={state.searchTerm}
                onChange={(e) => {
              lastSearchKeyAtRef.current = Date.now();
              intents.onProductSearch(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void intents.onBarcodeScanSubmit();
              }
            }}
            onBlur={() => {
              window.setTimeout(() => {
                if (posScreenRef.current !== 'selling') return;
      if (profileOpenRef.current || freePriceModalOpenRef.current) return;
                const active = document.activeElement as HTMLElement | null;
                if (active?.tagName === 'SELECT') return;
                focusProductSearch();
              }, 50);
            }}
            className="pl-10 pr-11 py-2.5 xl:py-3 bg-gray-900 border-gray-800 text-white placeholder:text-gray-600 text-sm h-auto rounded-xl focus-visible:ring-brand/40"
          />
          {state.searchTerm ? (
            <button
              type="button"
              aria-label="Effacer la recherche"
              title="Effacer"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-500 hover:text-white hover:bg-gray-800 transition-colors"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                intents.onProductSearch('');
                focusProductSearch();
              }}
            >
              <X size={14} />
            </button>
          ) : (
            <ScanLine size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
          )}
        </div>
        </div>

      {(state.freePriceCategories.length > 0 ||
        state.specialSaleProducts.length > 0) && (
        <div className="px-3 xl:px-5 pb-3">
          <div className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mb-2">
            Vente libre &amp; Services
          </div>
          <div
            className="flex gap-2 overflow-x-auto pb-0.5"
            style={{ scrollbarWidth: 'none' }}
          >
            {state.freePriceCategories.map((category) => (
              <button
                key={`free-price-${category.id}`}
                type="button"
                onClick={() => setFreePriceCategory(category)}
                className="group flex h-24 w-28 flex-none overflow-hidden rounded-xl border border-gray-800 bg-gray-900 text-left transition-all hover:border-[#F4A261]/60 active:scale-95"
              >
                <span
                  className="w-[3px] shrink-0 rounded-l-xl"
                  style={{ backgroundColor: FREE_PRICE_ACCENT }}
                />
                <span className="flex min-w-0 flex-1 flex-col justify-between p-2.5">
                  <span
                    className="text-[9px] font-black uppercase tracking-widest"
                    style={{ color: FREE_PRICE_ACCENT }}
                  >
                    {category.name}
                  </span>
                  <span className="line-clamp-2 text-[11px] font-semibold leading-tight text-gray-200">
                    {defaultFreePriceDesignation(category.name)}
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 transition-colors group-hover:text-[#F4A261]">
                    Prix TTC libre →
                  </span>
                </span>
              </button>
            ))}
            {state.specialSaleProducts.map((product, index) => {
              const accent = specialProductAccent(index);
              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => intents.onProductAddToCart(product.id)}
                  className="flex-none w-28 h-24 bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl overflow-hidden transition-all active:scale-95 flex text-left group"
                >
                  <span
                    className="w-[3px] shrink-0 rounded-l-xl"
                    style={{ backgroundColor: accent }}
                  />
                  <span className="flex-1 flex flex-col justify-between p-2.5 min-w-0">
                    <span
                      className="text-[9px] font-black uppercase tracking-widest"
                      style={{ color: accent }}
                    >
                      {categoryLabel(product.categoryId)}
                    </span>
                    <span className="text-[11px] font-semibold text-gray-200 leading-tight line-clamp-2">
                      {product.name}
                    </span>
                    <span className="text-[10px] font-bold tabular-nums text-gray-400">
                      {formatPrice(
                        salePriceTtcFromHt(product.salePriceHT, product.taxRate || 0),
                        product.currency,
                      )}
                      <span className="ml-1 text-[8px] font-semibold uppercase tracking-wide text-gray-600">
                        TTC
                      </span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-3 xl:px-5 flex gap-1.5 pb-2 xl:pb-3 flex-nowrap overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <button
            onClick={() => {
            intents.onCategorySelected("");
            if (state.searchTerm) intents.onProductSearch("");
          }}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all shrink-0 ${
            !state.selectedCategoryId ? 'bg-brand text-white' : 'bg-gray-900 text-gray-500 hover:bg-gray-800'
          }`}
        >
          Tous
          </button>
        {state.categories.map((category) => (
          <button
            key={category.id}
            onClick={() => {
              intents.onCategorySelected(category.id);
              if (state.searchTerm) intents.onProductSearch("");
            }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all shrink-0 ${
              state.selectedCategoryId === category.id
                ? 'bg-brand text-white'
                : 'bg-gray-900 text-gray-500 hover:bg-gray-800 hover:text-gray-200'
            }`}
          >
            {category.name}
          </button>
        ))}
        </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 xl:px-5 pb-4 xl:pb-5 min-w-0">
            <div className="grid grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 xl:gap-3 items-stretch">
          {state.products.map((product) => {
            const stock = product.branchStock?.availableStock ?? product.branchStock?.stock ?? 0;
            const min = product.branchStock?.minimumStock ?? 10;
            const isZero = stock <= 0;
            return (
            <div key={product.id} className="relative flex flex-col min-w-0">
              <button
                type="button"
                onClick={() => {
                  intents.onProductAddToCart(product.id);
                }}
                className={`w-full h-full text-left bg-gray-900 border rounded-xl overflow-hidden transition-all flex flex-col ${
                  isZero
                    ? 'border-red-900/50 hover:border-red-700/50 hover:shadow-lg active:scale-95'
                    : 'border-gray-800 hover:border-brand/50 hover:shadow-lg hover:shadow-brand/10 active:scale-95'
                }`}
              >
                <div className={`aspect-square w-full bg-gray-800 overflow-hidden shrink-0 ${isZero ? 'opacity-50' : ''}`}>
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-700">
                      <Package className="w-8 h-8" />
      </div>
                  )}
                </div>
                <div className="p-2.5 flex flex-col flex-1 min-h-[4.5rem]">
                  <div className={`text-xs font-medium line-clamp-2 leading-snug flex-1 ${isZero ? 'text-gray-500' : 'text-gray-300'}`}>
                    {product.name}
                  </div>
                  <div className="flex items-center justify-between mt-1.5 gap-1">
                    <span className={`text-sm font-bold tabular-nums ${isZero ? 'text-gray-600' : 'text-brand'}`}>
                      {formatPrice(salePriceTtcFromHt(product.salePriceHT, product.taxRate || 0), product.currency)}
                      <span className={`ml-1 text-[9px] font-semibold uppercase tracking-wide ${isZero ? 'text-gray-700' : 'text-brand/70'}`}>
                        TTC
                      </span>
                    </span>
                    <StockHealth stock={stock} min={min} compact />
                  </div>
                  <div className="text-[9px] text-gray-700 mt-0.5 font-mono tabular-nums">{product.sku ?? product.id}</div>
                </div>
              </button>
            </div>
          );})}

          {state.products.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-16 text-gray-600">
              <Search className="w-10 h-10 mb-3 text-gray-800" />
              <p className="text-sm font-medium">Aucun résultat</p>
              <p className="text-xs text-gray-700 mt-1">
                {state.searchTerm ? `Aucun résultat pour « ${state.searchTerm} »` : 'Scannez le code-barres ou modifiez la recherche'}
              </p>
        </div>
          )}
        </div>
        </div>
      </div>
  );

  const renderSuccessScreen = () => {
    const completedReceipt = state.receiptHistory.find((r) => r.receiptNumber === lastCompletedReceiptId)
      ?? state.receiptHistory[state.receiptHistory.length - 1];
    const paymentLabel = state.paymentMethod === PaymentMethod.CARD
      ? t('pos.checkout.card')
      : state.paymentMethod === PaymentMethod.CHEQUE
        ? t('pos.checkout.cheque')
        : t('pos.checkout.cash');
    const receiptId = lastCompletedReceiptId || state.receiptNumber;
    const paidTotal = completedReceipt?.grandTotal ?? state.grandTotal;
    const cashGiven = completedReceipt?.cashGiven ?? state.cashGiven;
    const change = completedReceipt?.cashReturned ?? state.cashToReturn;

    return (
    <div className="flex-1 flex items-center justify-center bg-gray-950">
      <div className="text-center space-y-6 max-w-sm w-full px-6">
        <div className="w-24 h-24 bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto border-4 border-emerald-600/40">
          <CheckCircle size={44} className="text-emerald-400" />
        </div>

        <div>
          <div className="text-3xl font-bold text-white">{t('pos.success.title')}</div>
          <div className="text-gray-500 mt-1 text-sm font-mono">{receiptId}</div>
          <div className="text-5xl font-bold tabular-nums text-brand mt-4 leading-none">{formatPrice(paidTotal, 'TND')}</div>
          <div className="text-lg text-gray-500 mt-1">TND</div>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 text-left space-y-2.5">
          {[
            [t('pos.success.cashier'), state.cashierName],
            [t('pos.success.branch'), branchName],
            [t('pos.success.date'), (completedReceipt?.createdAt ?? new Date()).toLocaleString('fr-TN')],
            [t('pos.success.payment'), paymentLabel],
          ].map(([k, v]) => (
            <div key={String(k)} className="flex justify-between text-sm">
              <span className="text-gray-500">{k}</span>
              <span className="text-gray-200">{v}</span>
            </div>
          ))}
          {state.paymentMethod === PaymentMethod.CASH && cashGiven > 0 && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('pos.checkout.cash_given')}</span>
                <span className="text-gray-200 tabular-nums">{formatPrice(cashGiven, 'TND')}</span>
              </div>
              <div className="flex justify-between text-sm font-semibold border-t border-gray-800 pt-2">
                <span className="text-emerald-500">{t('pos.checkout.change_due')}</span>
                <span className="text-emerald-400 tabular-nums">{formatPrice(change, 'TND')}</span>
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2.5">
            <button
            type="button"
            onClick={() => {
              if (!completedReceipt) return;
              void intents.onReceiptPrint(completedReceipt);
            }}
            className="flex flex-col items-center gap-1.5 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl font-medium transition-colors text-xs"
          >
            <Printer size={16} />{t('pos.success.print')}
            </button>
            <button
            type="button"
            onClick={() => {
              const receipt = completedReceipt;
              if (!receipt) return;
              setInvoiceSale({
                id: receipt.receiptNumber,
                date: receipt.createdAt.toLocaleDateString('fr-TN'),
                time: receipt.createdAt.toLocaleTimeString('fr-TN'),
                cashier: state.cashierName,
                branch: branchName,
                paymentMethod: paymentLabel,
                items: receipt.items.map((item) => ({
                  name: item.productName,
                  sku: item.productId,
                  qty: item.quantity,
                  unitPrice: item.unitPrice,
                  tax: item.taxRate * 100,
                  discount: item.productDiscountPercentage,
                })),
              });
            }}
            className="flex flex-col items-center gap-1.5 py-3 bg-gray-800 hover:bg-gray-700 text-brand border border-brand/20 rounded-xl font-medium transition-colors text-xs"
          >
            <FileText size={16} />{t('pos.success.invoice')}
            </button>
            <button
            type="button"
            onClick={() => setPosScreen('selling')}
            className="flex flex-col items-center gap-1.5 py-3 bg-brand hover:bg-brand-light text-white rounded-xl font-bold transition-colors text-xs"
            >
            <Plus size={16} />{t('pos.success.new_sale')}
            </button>
          </div>
        </div>
    </div>
  );};

  const confirmStockBarcodePrint = async () => {
    if (!barcodePrintTarget || barcodePrintBusy) return;
    const setup = readTerminalSetup();
    const decision = decideStockBarcodePrint({
      confirmed: true,
      productId: barcodePrintTarget.id,
      barcode: barcodePrintTarget.barcode,
      sku: barcodePrintTarget.sku,
      barcodePrinterName: setup?.barcodePrinterName,
    });
    if (!decision.ok) {
      setBarcodePrintMessage(
        decision.reason === 'missing_binding'
          ? t('pos.inventory.print_barcode_missing_binding')
          : t('pos.inventory.print_barcode_missing_code'),
      );
      setBarcodePrintTarget(null);
      return;
    }
    setBarcodePrintBusy(true);
    const result = await printProductBarcodeLabel(accessToken, decision.productId, {
      printerName: decision.printerName,
      includeProductName: setup?.barcodeIncludeName !== false,
      includePriceTtc: setup?.barcodeIncludePrice !== false,
    });
    setBarcodePrintBusy(false);
    setBarcodePrintTarget(null);
    setBarcodePrintMessage(
      result.ok
        ? t('pos.inventory.print_barcode_ok', { printer: result.printer })
        : t('pos.inventory.print_barcode_error', { error: result.error }),
    );
  };

  const renderInventoryScreen = () => {
    const filtered = state.products.filter((p) => {
      const q = state.searchTerm.trim();
      if (!q) return true;
      const hay = buildProductSearchHaystack([
        p.name,
        p.description,
        p.sku,
        p.barcode,
        p.id,
      ]);
      return productSearchHaystackMatches(hay, q);
    });
    const pages = Math.max(1, Math.ceil(filtered.length / INV_PER_PAGE));
    const visible = filtered.slice((invPage - 1) * INV_PER_PAGE, invPage * INV_PER_PAGE);

    return (
    <div className="flex-1 flex flex-col bg-gray-950 overflow-hidden">
      <div className="p-5 border-b border-gray-800">
        <div className="flex items-center gap-3 mb-3">
          <PosBackButton onClick={() => setPosScreen('selling')} />
          <h2 className="text-sm font-bold text-gray-200">{t('pos.inventory.title')}</h2>
              </div>
        <div className="relative max-w-xl">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            placeholder={t('pos.inventory.search_placeholder')}
            value={state.searchTerm}
            onChange={(e) => { intents.onProductSearch(e.target.value); setInvPage(1); }}
            className="pl-10 py-3 bg-gray-900 border-gray-800 text-white placeholder:text-gray-600 text-sm h-auto rounded-xl"
          />
            </div>
        {barcodePrintMessage ? (
          <p className="mt-2 text-xs text-teal-300">{barcodePrintMessage}</p>
        ) : null}
            </div>
      <div className="flex-1 overflow-y-auto p-5">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-800">
            <tr>
              <th className="text-left py-2 text-xs text-gray-500 uppercase">SKU</th>
              <th className="text-left py-2 text-xs text-gray-500 uppercase">Produit</th>
              <th className="text-left py-2 text-xs text-gray-500 uppercase">Barcode</th>
              <th className="text-left py-2 text-xs text-gray-500 uppercase">Catégorie</th>
              <th className="text-right py-2 text-xs text-gray-500 uppercase">{t('pos.inventory.col_pu_ttc')}</th>
              <th className="text-right py-2 text-xs text-gray-500 uppercase">Stock</th>
              <th className="text-right py-2 text-xs text-gray-500 uppercase">Statut</th>
              <th className="text-right py-2 text-xs text-gray-500 uppercase">{t('pos.inventory.col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => {
              const stock = p.branchStock?.availableStock ?? 0;
              const min = p.branchStock?.minimumStock ?? 10;
              const priceTtc = salePriceTtcFromHt(p.salePriceHT, p.taxRate || 0);
              const canPrint = Boolean((p.barcode || p.sku || '').trim());
              return (
                <tr key={p.id} className="border-t border-gray-800/50 hover:bg-gray-900/50">
                  <td className="py-3 font-mono text-xs text-gray-600">{p.sku ?? p.id}</td>
                  <td className="py-3 text-gray-200">{p.name}</td>
                  <td className="py-3 font-mono text-xs text-gray-600">{p.barcode ?? '—'}</td>
                  <td className="py-3 text-xs text-gray-500">{categoryLabel(p.categoryId)}</td>
                  <td className="py-3 text-right tabular-nums text-gray-300">
                    {formatPrice(priceTtc, p.currency || 'TND')}
                  </td>
                  <td className="py-3 text-right tabular-nums text-gray-300">{stock}</td>
                  <td className="py-3 text-right"><StockHealth stock={stock} min={min} compact /></td>
                  <td className="py-3 text-right">
                    <button
                      type="button"
                      disabled={!canPrint}
                      onClick={() => {
                        setBarcodePrintMessage('');
                        setBarcodePrintTarget(p);
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-700 text-xs text-gray-300 hover:bg-gray-800 disabled:opacity-40"
                    >
                      <Printer size={12} />
                      {t('pos.inventory.print_barcode')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
            </div>
      <div className="px-5 py-4 border-t border-gray-800 flex items-center justify-between shrink-0">
        <span className="text-xs text-gray-600">{t('pos.inventory.products_count', { count: filtered.length })}</span>
        <div className="flex gap-2 items-center">
          <button type="button" disabled={invPage === 1} onClick={() => setInvPage((p) => Math.max(1, p - 1))} className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-gray-400 text-sm disabled:opacity-40">‹</button>
          <span className="text-sm text-gray-500 tabular-nums">{invPage}/{pages}</span>
          <button type="button" disabled={invPage >= pages} onClick={() => setInvPage((p) => Math.min(pages, p + 1))} className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-gray-400 text-sm disabled:opacity-40">›</button>
        </div>
      </div>
      {barcodePrintTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-900 p-5 space-y-4">
            <h3 className="text-sm font-bold text-gray-100">{t('pos.inventory.print_barcode_confirm_title')}</h3>
            <p className="text-sm text-gray-400">
              {t('pos.inventory.print_barcode_confirm_body', { name: barcodePrintTarget.name })}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={barcodePrintBusy}
                onClick={() => setBarcodePrintTarget(null)}
                className="px-3 py-1.5 rounded-lg border border-gray-700 text-sm text-gray-300"
              >
                {t('pos.inventory.print_barcode_cancel')}
              </button>
              <button
                type="button"
                disabled={barcodePrintBusy}
                onClick={() => void confirmStockBarcodePrint()}
                className="px-3 py-1.5 rounded-lg bg-teal-700 text-sm text-white"
              >
                {t('pos.inventory.print_barcode_confirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );};

  const renderHistoryScreen = () => {
    const filteredHistory = state.receiptHistory.filter((r) => {
      const q = historySearch.trim().toLowerCase();
      if (!q) return true;
      return (
        r.receiptNumber.toLowerCase().includes(q) ||
        r.cashierName.toLowerCase().includes(q)
      );
    });

    return (
    <div className="flex-1 flex flex-col bg-gray-950 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-800">
        <div className="flex items-center gap-3 mb-3">
          <PosBackButton onClick={() => setPosScreen('selling')} />
          <History size={16} className="text-brand shrink-0" />
          <h2 className="text-sm font-bold text-gray-200">{t('pos.history.title')}</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder={t('pos.history.search_placeholder')}
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              className="pl-9 py-2 bg-gray-900 border-gray-800 text-white placeholder:text-gray-600 text-sm h-auto rounded-xl"
            />
          </div>
          <span className="text-xs text-gray-500 shrink-0">{t('pos.history.sales_count', { count: filteredHistory.length })}</span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-5">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-800 sticky top-0 bg-gray-950">
            <tr>
              <th className="text-left py-2 text-xs text-gray-500 uppercase">{t('pos.history.receipt')}</th>
              <th className="text-left py-2 text-xs text-gray-500 uppercase">{t('pos.history.date')}</th>
              <th className="text-left py-2 text-xs text-gray-500 uppercase">{t('pos.history.cashier')}</th>
              <th className="text-right py-2 text-xs text-gray-500 uppercase">{t('pos.history.items')}</th>
              <th className="text-right py-2 text-xs text-gray-500 uppercase">{t('pos.history.total')}</th>
              <th className="text-right py-2 text-xs text-gray-500 uppercase">{t('pos.history.status')}</th>
              <th className="text-right py-2 text-xs text-gray-500 uppercase" />
            </tr>
          </thead>
          <tbody>
            {filteredHistory.length > 0 ? (
              filteredHistory.map((r) => (
                <tr key={r.id} className="border-t border-gray-800/50 hover:bg-gray-900/50">
                  <td className="py-3 font-mono text-xs text-brand">{r.receiptNumber}</td>
                  <td className="py-3 text-gray-400 text-xs tabular-nums">{formatReceiptDateTime(r.createdAt)}</td>
                  <td className="py-3 text-gray-300 text-xs">{r.cashierName}</td>
                  <td className="py-3 text-right tabular-nums text-gray-400">{r.items.length}</td>
                  <td className="py-3 text-right tabular-nums text-gray-200">{formatPrice(r.grandTotal, 'TND')}</td>
                  <td className="py-3 text-right">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isReceiptRefunded(r, refundedIds)
                        ? 'bg-red-950 text-red-400'
                        : r.status === 'PARTIAL'
                          ? 'bg-amber-950 text-amber-300'
                          : 'bg-emerald-950 text-emerald-400'
                    }`}>
                      {isReceiptRefunded(r, refundedIds)
                        ? t('pos.history.refunded')
                        : r.status === 'PARTIAL'
                          ? t('pos.history.partial')
                          : t('pos.history.completed')}
                    </span>
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        disabled={printingReceiptId === r.id}
                        onClick={() => {
                          setPrintingReceiptId(r.id);
                          void intents.onReceiptPrint(r).finally(() => {
                            setPrintingReceiptId((current) =>
                              current === r.id ? null : current,
                            );
                          });
                        }}
                        className="text-xs text-gray-300 hover:text-white font-medium flex items-center gap-1 disabled:opacity-50"
                      >
                        <Printer size={11} />
                        {printingReceiptId === r.id
                          ? t('pos.print.config_saving')
                          : t('pos.history.print')}
                      </button>
              <button
                        type="button"
                        onClick={() => setInvoiceSale({
                          id: r.receiptNumber,
                          date: r.createdAt.toLocaleDateString('fr-TN'),
                          time: r.createdAt.toLocaleTimeString('fr-TN'),
                          cashier: r.cashierName,
                          branch: branchName,
                          paymentMethod: t('pos.checkout.cash'),
                          items: r.items.map((item) => ({
                            name: item.productName,
                            sku: item.productId,
                            qty: item.quantity,
                            unitPrice: item.unitPrice,
                            tax: item.taxRate * 100,
                            discount: item.productDiscountPercentage,
                          })),
                        })}
                        className="text-xs text-brand hover:text-brand-light font-medium flex items-center gap-1"
                      >
                        <FileText size={11} />
                        {t('pos.history.invoice')}
              </button>
                      {!isReceiptRefunded(r, refundedIds) && (
              <button
                          type="button"
                onClick={() => {
                            setRefundPrefillReceipt(r);
                            intents.onReceiptSelected(r);
                            setRefundStartAtReview(true);
                            setPosScreen('refund');
                          }}
                          className="text-xs text-amber-400 hover:text-amber-300 font-medium flex items-center gap-1"
                        >
                          <RefreshCw size={11} />
                          {t('pos.history.refund')}
              </button>
                      )}
            </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="py-12 text-center text-gray-600 text-sm">Aucun reçu pour cette session</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );};

  const renderRefundScreen = () => (
    <PosRefundScreen
      receipts={state.receiptHistory}
      selectedReceipt={refundPrefillReceipt ?? state.selectedReceipt}
      cashierName={state.cashierName}
      branchName={branchName}
      isProcessing={state.isProcessingPayment}
      lookupError={state.errors.general || state.errors.payment}
      startAtReview={refundStartAtReview}
      refundedIds={refundedIds}
      onRefunded={(id) => {
        setRefundedIds((prev) => {
          if (prev.has(id)) return prev;
          const next = new Set(prev);
          next.add(id);
          return next;
        });
      }}
      onLoadHistory={() => {
        void intents.onLoadReceiptHistory();
      }}
      onLookup={async (id) => {
        setRefundPrefillReceipt(null);
        setRefundStartAtReview(false);
        return intents.onLookupReceipt(id);
      }}
      onSelectReceipt={(r) => {
        setRefundPrefillReceipt(r);
        intents.onReceiptSelected(r);
      }}
      onClear={() => {
        intents.onClearRefund();
        setRefundPrefillReceipt(null);
        setRefundStartAtReview(false);
      }}
      onProcessRefund={(opts) => intents.onProcessRefund(opts)}
      products={state.products}
      onDone={() => {
        intents.onClearRefund();
        setRefundPrefillReceipt(null);
        setRefundStartAtReview(false);
        setPosScreen('selling');
      }}
    />
  );

  const renderMainContent = () => {
    if (posScreen === 'success') return renderSuccessScreen();
    if (posScreen === 'checkout') {
      const cashNum = effectiveCashGiven(
        state.cashKeypadValue,
        state.cashGiven,
        state.grandTotal,
      );
      const change = cashNum - state.grandTotal;
      const canPay =
        state.paymentMethod !== PaymentMethod.CASH ||
        canConfirmCashPay(
          state.cashKeypadValue,
          state.cashGiven,
          state.grandTotal,
        );
      const taxPct =
        state.cart.length > 0
          ? Math.round((state.cart[0].taxRate || 0.19) * 100)
          : 19;

      return (
        <div className="flex-1 flex overflow-y-auto overflow-x-hidden bg-gray-950 min-h-0">
          {/* LEFT: Order summary — Figma POSCheckout; narrow on 1024×768 */}
          <div className="w-[280px] xl:w-[400px] flex flex-col border-r border-gray-800 bg-[#0C1821] shrink-0 min-h-0">
            <div className="px-3 xl:px-6 py-3 xl:py-5 border-b border-gray-800 flex items-center gap-2 xl:gap-3 shrink-0">
              <PosBackButton
                size="compact"
                onClick={() => setPosScreen('selling')}
                label={t('pos.checkout.back_to_cart')}
              />
              <div>
                <div className="text-white font-bold text-base leading-tight">{t('pos.checkout.encaissement')}</div>
                <div className="text-gray-500 text-xs font-mono mt-0.5">{state.receiptNumber}</div>
            </div>
          </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
              {state.cart.map((item, idx) => {
                const unitPriceTtc = salePriceTtcFromHt(item.unitPrice, item.taxRate || 0);
                const lineTTC = cartLineTotalTtc(item);
                const lineDiscTtc = item.quantity * unitPriceTtc - lineTTC;
                return (
                  <div key={`${item.productId}-${idx}`} className="flex items-start gap-3 py-2 border-b border-gray-800/60 last:border-0">
                    <div className="w-5 h-5 rounded-md bg-gray-800 flex items-center justify-center text-[10px] text-gray-500 font-bold shrink-0 mt-0.5">
                      {idx + 1}
        </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-200 leading-snug truncate">{item.productName}</div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                        <span className="tabular-nums">×{item.quantity}</span>
                        <span className="tabular-nums">@ {formatPrice(unitPriceTtc, 'TND')}</span>
                        {item.productDiscountPercentage > 0 && (
                          <span className="text-amber-500 font-semibold">−{item.productDiscountPercentage}%</span>
                        )}
            </div>
            </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-bold tabular-nums text-white">{formatPrice(lineTTC, 'TND')}</div>
                      {lineDiscTtc > 0.0005 && (
                        <div className="text-[10px] text-amber-500 tabular-nums">
                          −{formatPrice(lineDiscTtc, 'TND')} rem.
            </div>
                      )}
          </div>
        </div>
                );
              })}
            </div>

            <div className="px-5 pb-5 pt-3 border-t border-gray-800 space-y-2">
              <div className="flex justify-between text-sm text-gray-500">
                <span>{t('pos.checkout.subtotal_ttc')}</span>
                <span className="tabular-nums">{formatPrice(subtotalTtc, 'TND')}</span>
          </div>

              {productRemisesTotal > 0 && (
                <div className="flex justify-between text-sm text-amber-500/70">
                  <span className="flex items-center gap-1.5"><Tag size={11} />{t('pos.cart.product_discounts')}</span>
                  <span className="tabular-nums">−{formatPrice(productRemisesTotal, 'TND')}</span>
        </div>
      )}

              <div className="flex items-center justify-between bg-amber-950/40 border border-amber-800/40 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <Tag size={12} className="text-amber-400 shrink-0" />
                  <span className="text-sm text-amber-300 font-medium">{t('pos.checkout.cart_discount')}</span>
                </div>
                <div className="flex items-center gap-2">
                  {cartRemiseTtc > 0 && (
                    <span className="text-sm tabular-nums text-amber-400 font-semibold">
                      −{formatPrice(cartRemiseTtc, 'TND')}
                    </span>
                  )}
                  <select
                    value={state.discountPercentage}
                    onChange={(e) => intents.onDiscountChanged(parseInt(e.target.value, 10))}
                    className="bg-gray-800 border border-amber-700 rounded-lg text-sm text-amber-400 font-bold px-2 py-1 appearance-none focus:outline-none focus:border-amber-500 cursor-pointer"
                  >
                    {DISC_OPTIONS.map((d) => (
                      <option key={d} value={d}>{d}%</option>
                    ))}
                  </select>
                </div>
              </div>

              {(productRemisesTotal + cartRemiseTtc) > 0 && (
                <div className="flex justify-between text-sm font-semibold text-amber-400">
                  <span>{t('pos.checkout.total_discounts')}</span>
                  <span className="tabular-nums">−{formatPrice(productRemisesTotal + cartRemiseTtc, 'TND')}</span>
                </div>
              )}

              <div className="flex justify-between text-sm text-gray-500">
                <span>{t('pos.checkout.vat', { rate: taxPct })}</span>
                <span className="tabular-nums">{formatPrice(state.taxAmount || 0, 'TND')}</span>
              </div>

              <div className="bg-brand/10 border border-brand/20 rounded-xl px-3 xl:px-4 py-2.5 xl:py-3 mt-1">
                <div className="text-xs text-brand font-semibold uppercase tracking-widest mb-1">{t('pos.checkout.total_ttc')}</div>
                <div className="text-2xl xl:text-3xl font-bold tabular-nums text-brand leading-none">
                  {formatPrice(state.grandTotal, 'TND').replace(/\s*TND\s*$/i, '').trim()}
                </div>
                <div className="text-sm text-brand/70 mt-0.5">TND</div>
              </div>

              <div className="flex items-center gap-2 pt-1">
          <input
            type="checkbox"
                  id="printRcpt"
            checked={state.shouldPrintReceipt}
            onChange={intents.onTogglePrintReceipt}
                  className="accent-brand w-4 h-4"
          />
                <label htmlFor="printRcpt" className="text-sm text-gray-500 cursor-pointer">
                  {t('pos.checkout.print_receipt')}
        </label>
              </div>
            </div>
          </div>

          {/* RIGHT: Payment entry */}
          <div className="flex-1 flex flex-col bg-gray-950 px-3 xl:px-8 py-3 xl:py-6 gap-3 xl:gap-5 min-w-0 overflow-y-auto">
            <div className="shrink-0">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 xl:mb-3">
                {t('pos.checkout.payment_mode')}
              </div>
              <div className="grid grid-cols-3 gap-2 xl:gap-3">
                {([
                  [PaymentMethod.CASH, t('pos.checkout.cash'), '💵'],
                  [PaymentMethod.CARD, t('pos.checkout.card'), '💳'],
                  [PaymentMethod.CHEQUE, t('pos.checkout.cheque'), '📄'],
                ] as const).map(([m, label, ico]) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => intents.onPaymentMethodSelected(m)}
                    className={`py-2.5 xl:py-3.5 rounded-xl text-xs xl:text-sm font-bold flex flex-col items-center gap-1 xl:gap-1.5 transition-all ${
                      state.paymentMethod === m
                        ? 'bg-brand text-white shadow-lg shadow-brand/30 scale-[1.02]'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                    }`}
                  >
                    <span className="text-xl xl:text-2xl leading-none">{ico}</span>
                    <span>{label}</span>
                  </button>
                ))}
        </div>
      </div>

            {state.paymentMethod === PaymentMethod.CASH ? (
              <div className="flex flex-col xl:flex-row gap-3 xl:gap-5 flex-1 min-h-0">
                <div className="flex flex-col gap-2 xl:gap-3 flex-1 min-w-0">
                  <div className="bg-gray-900 border border-gray-700 rounded-xl px-4 xl:px-5 py-3 xl:py-4 text-right shrink-0">
                    <div className="text-xs text-gray-600 font-semibold uppercase tracking-widest mb-1">
                      {t('pos.checkout.cash_given')}
                    </div>
                    <div className="flex items-baseline justify-end gap-2">
                      <span className="text-3xl xl:text-4xl font-bold tabular-nums text-white tracking-tight">
                        {state.cashKeypadValue || '0'}
                      </span>
                      <span className="text-lg text-gray-500">TND</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-1.5 xl:gap-2 flex-1 content-start">
                    {['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', '⌫'].map((k) => (
          <button
                        key={k}
                        type="button"
                        onClick={() => {
                          if (k === '⌫') intents.onCashKeypadBackspace();
                          else intents.onCashKeypadPress(k);
                        }}
                        className={`rounded-xl text-lg xl:text-xl font-bold transition-all active:scale-95 select-none min-h-[44px] xl:min-h-[56px] ${
                          k === '⌫'
                            ? 'bg-red-950 border border-red-900 text-red-400 hover:bg-red-900'
                            : k === '.'
                              ? 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'
                              : 'bg-gray-800 border border-gray-700 text-white hover:bg-gray-700 hover:border-gray-600'
                        }`}
                      >
                        {k}
          </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-4 gap-2 shrink-0">
                    {[5, 10, 20, 50].map((a) => (
        <button
                        key={a}
                        type="button"
                        onClick={() => intents.onCashGivenChanged(a)}
                        className="py-2 xl:py-2.5 bg-gray-800 hover:bg-brand/20 border border-gray-700 hover:border-brand/50 text-gray-300 hover:text-white text-sm font-bold rounded-xl transition-all"
                      >
                        {a}
        </button>
                    ))}
                  </div>
      </div>

                <div className="w-full xl:w-48 flex flex-row xl:flex-col gap-2 xl:gap-3 shrink-0">
                  <div
                    className={`flex-1 rounded-xl border-2 flex flex-col items-center justify-center p-3 xl:p-4 transition-all min-h-[88px] ${
                      state.cashKeypadValue
                        ? canPay
                          ? 'bg-emerald-950 border-emerald-700'
                          : 'bg-red-950 border-red-800'
                        : 'bg-gray-900 border-gray-800'
                    }`}
                  >
                    <div
                      className={`text-[10px] xl:text-xs font-bold uppercase tracking-widest mb-1 xl:mb-3 ${
                        state.cashKeypadValue
                          ? canPay
                            ? 'text-emerald-500'
                            : 'text-red-500'
                          : 'text-gray-600'
                      }`}
                    >
                      {state.cashKeypadValue
                        ? canPay
                          ? t('pos.checkout.change_due')
                          : t('pos.checkout.insufficient')
                        : t('pos.checkout.change_due')}
        </div>
                    <div
                      className={`text-2xl xl:text-4xl font-bold tabular-nums leading-none text-center ${
                        state.cashKeypadValue
                          ? canPay
                            ? 'text-emerald-400'
                            : 'text-red-400'
                          : 'text-gray-700'
                      }`}
                    >
                      {state.cashKeypadValue ? formatPrice(Math.abs(change), 'TND').replace(/\s*TND\s*$/i, '').trim() : '—'}
                    </div>
                    {state.cashKeypadValue && (
                      <div className={`text-sm mt-1 ${canPay ? 'text-emerald-600' : 'text-red-600'}`}>TND</div>
                    )}
                    {state.cashKeypadValue && !canPay && (
                      <div className="text-xs text-red-500 mt-2 text-center leading-snug">
                        {t('pos.checkout.missing_amount', { amount: formatPrice(Math.abs(change), 'TND') })}
    </div>
                    )}
                  </div>

                  <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 text-center w-36 xl:w-auto shrink-0">
                    <div className="text-[10px] text-gray-600 uppercase font-bold tracking-widest">
                      {t('pos.checkout.to_collect')}
        </div>
                    <div className="text-lg xl:text-xl font-bold tabular-nums text-brand mt-1">
                      {formatPrice(state.grandTotal, 'TND').replace(/\s*TND\s*$/i, '').trim()}
      </div>
                    <div className="text-xs text-gray-600">TND</div>
                </div>
                </div>
                </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center gap-5">
                <div className="w-20 h-20 rounded-2xl bg-gray-800 flex items-center justify-center">
                  <span className="text-5xl">{state.paymentMethod === PaymentMethod.CARD ? '💳' : '📄'}</span>
              </div>
                <div className="text-center">
                  <div className="text-white font-bold text-xl">
                    {state.paymentMethod === PaymentMethod.CARD
                      ? t('pos.checkout.card_title')
                      : t('pos.checkout.cheque_title')}
                  </div>
                  <div className="text-gray-500 text-sm mt-1">
                    {state.paymentMethod === PaymentMethod.CARD
                      ? t('pos.checkout.card_hint')
                      : t('pos.checkout.cheque_hint')}
                  </div>
                </div>
                <div className="bg-brand/10 border border-brand/20 rounded-2xl px-8 py-4 text-center">
                  <div className="text-xs text-brand uppercase font-bold tracking-widest">{t('pos.checkout.amount_due')}</div>
                  <div className="text-4xl font-bold tabular-nums text-brand mt-1">{formatPrice(state.grandTotal, 'TND')}</div>
                </div>
                <ComingLaterBanner
                  feature={
                    state.paymentMethod === PaymentMethod.CARD
                      ? t('pos.checkout.card')
                      : t('pos.checkout.cheque')
                  }
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 xl:gap-3 shrink-0 sticky bottom-0 bg-gray-950 pt-1">
              <button
                type="button"
                onClick={() => setPosScreen('selling')}
                className="h-12 xl:h-14 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 font-bold rounded-xl transition-all text-xs xl:text-sm flex items-center justify-center gap-2"
              >
                <ArrowLeft size={16} />{t('pos.checkout.back_to_cart')}
              </button>
              <button
                type="button"
                onClick={() => void intents.onConfirmPurchase()}
                disabled={
                  state.isProcessingPayment ||
                  state.cart.length === 0 ||
                  !canPay ||
                  state.paymentMethod !== PaymentMethod.CASH
                }
                className="h-12 xl:h-14 bg-brand hover:bg-brand-light disabled:opacity-25 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all text-sm xl:text-lg flex items-center justify-center gap-2 shadow-xl shadow-brand/30 active:scale-[0.98]"
              >
                <Check size={18} />
                {t('pos.checkout.pay')} — {formatPrice(state.grandTotal, 'TND')}
              </button>
            </div>
            {(state.errors.payment || state.errors.general) && (
              <div className="p-2 bg-red-950 text-red-400 text-xs rounded-lg shrink-0">
                {state.errors.payment || state.errors.general}
            </div>
          )}
        </div>
        </div>
      );
    }
    if (posScreen === 'refund') return renderRefundScreen();
    if (posScreen === 'inventory') return renderInventoryScreen();
    if (posScreen === 'history') return renderHistoryScreen();
    if (posScreen === 'cloture') {
      if (userRole === 'cashier') {
        return (
          <div className="flex-1 flex flex-col bg-brand-dark min-h-0">
            <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-3 shrink-0">
              <PosBackButton onClick={() => setPosScreen('selling')} />
              <span className="text-sm font-bold text-white">Clôture de caisse</span>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <XCircle size={48} className="text-red-400 mb-4" />
              <h2 className="text-lg font-bold text-white">Accès refusé</h2>
              <p className="text-sm text-gray-500 mt-2 max-w-md">
                La clôture de caisse est réservée aux managers et administrateurs.
              </p>
      </div>
    </div>
  );
      }
      return <PosClotureScreen onBack={() => setPosScreen('selling')} />;
    }
    if (posScreen === 'stockcount') {
      return (
        <PosStockCountScreen
          onBack={() => {
            setPosScreen('selling');
            refreshActiveStockCount();
          }}
          userRole={userRole}
          branchName={branchName}
        />
      );
    }

    // selling (default)
    return (
      <div className="flex-1 flex overflow-hidden min-h-0 min-w-0">
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {renderProductSection()}
        </div>
        <div className="w-[360px] max-w-[42vw] xl:w-[480px] xl:max-w-none shrink-0 flex flex-col bg-gray-900 border-l border-gray-800 overflow-hidden min-w-0">
          <div className="px-2 xl:px-4 py-2 xl:py-2.5 border-b border-gray-800 flex items-center justify-between shrink-0 gap-1 min-w-0">
            <div className="flex items-center gap-0.5 bg-gray-800 rounded-lg p-0.5 min-w-0 overflow-x-auto">
              <button type="button" className="px-2 xl:px-3 py-1.5 bg-brand text-white text-xs font-bold rounded-md flex items-center gap-1 xl:gap-1.5 shrink-0">
                <ShoppingCart size={11} />
                <span className="hidden sm:inline">{t('pos.cart.tab_cart')}</span>
              </button>
              <button
                type="button"
            onClick={() => {
                  setRefundStartAtReview(false);
                  void intents.onEnterRefundMode();
                  setPosScreen('refund');
                }}
                className="px-2 xl:px-3 py-1.5 text-gray-400 hover:text-amber-300 text-xs font-semibold rounded-md flex items-center gap-1 xl:gap-1.5 transition-colors shrink-0"
              >
                <RefreshCw size={11} />
                <span className="hidden sm:inline">{t('pos.cart.tab_refund')}</span>
              </button>
              <button
                type="button"
                onClick={() => setPosScreen('history')}
                className="px-2 xl:px-3 py-1.5 text-gray-400 hover:text-gray-200 text-xs font-semibold rounded-md flex items-center gap-1 xl:gap-1.5 transition-colors shrink-0"
              >
                <History size={11} />
                <span className="hidden sm:inline">{t('pos.cart.tab_history')}</span>
              </button>
      </div>
            <div className="flex items-center gap-1.5 xl:gap-2 shrink-0">
              <span className="text-[10px] text-gray-600 font-mono hidden xl:inline">{state.receiptNumber}</span>
              {state.cart.length > 0 && (
                <button
                  type="button"
                  onClick={intents.onClearCart}
                  className="flex items-center gap-1 px-2 xl:px-2.5 py-1 bg-red-950/60 hover:bg-red-900/80 border border-red-800/60 hover:border-red-600 text-red-400 hover:text-red-300 text-[11px] font-bold rounded-lg transition-all active:scale-95"
                >
                  <Trash2 size={11} />
                  <span className="hidden sm:inline">{t('pos.cart.cancel')}</span>
                </button>
              )}
    </div>
          </div>
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {renderCartSection()}
        </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-gray-950">
      {renderHeader()}
      {renderMainContent()}
      {renderNotification()}
      {freePriceCategory && (
        <FreePriceProductModal
          categoryName={freePriceCategory.name}
          onAdd={(designation, priceTtc) =>
            intents.onFreePriceProductAddToCart(
              freePriceCategory.id,
              designation,
              priceTtc,
            )
          }
          onClose={() => {
            setFreePriceCategory(null);
            window.setTimeout(focusProductSearch, 40);
          }}
        />
      )}
      {invoiceSale && <InvoiceModal sale={invoiceSale} onClose={() => setInvoiceSale(null)} />}
      <PosProfileModal
        open={profileOpen}
        displayName={profileFullName}
        initialName={profileFullName}
        initialEmail={userSession?.user.email ?? ''}
        saving={profileSaving}
        error={profileError}
        onClose={() => {
          if (!profileSaving) setProfileOpen(false);
        }}
        onSubmit={onSaveProfile}
      />
    </div>
  );
};

export default CashierSellingScreen; 